import { useEffect, useMemo, useRef, useState } from 'react'

import type { AgentStyle } from '~/game/agentCharacters'
import { startAmbient } from '~/game/ambient'
import { PhaserGame } from '~/game/PhaserGame'
import { EventBus } from '~/realtime/bus'
import { followAgentLooks } from '~/state/agentLooks'
import { theme } from '~/theme'
import { DetailPanel, Ticker } from '~/ui/GameView'
import { PeopleDirectory } from '~/ui/PeopleDirectory'
import { withDemoMeetings } from './demoMeetings'
import { demoSnapshot } from './demoSnapshot'

/** What the demo agents say they are doing (the ticker and the floor's thoughts). */
const DEMO_WORK = [
  'Reviewing the launch checklist',
  'Drafting the weekly update',
  'Sorting the support inbox',
  'Checking the Q3 numbers',
]

/**
 * No-login showcase + engine smoke test: renders the pixel office over a
 * fictional org and runs the same ambient choreography the real app uses, so
 * the whole thing comes alive with zero backend. Click someone for their
 * card; "People" opens the directory (the avatars there follow the looks).
 */
export function DemoStage() {
  const snapshot = useMemo(() => withDemoMeetings(demoSnapshot()), [])
  const busRef = useRef<EventBus>()
  if (!busRef.current) {
    busRef.current = new EventBus()
    followAgentLooks(busRef.current)
  }
  const bus = busRef.current
  const [focusedId, setFocusedId] = useState<string | null>(null)
  const [directoryOpen, setDirectoryOpen] = useState(false)

  useEffect(() => startAmbient(snapshot, bus), [snapshot, bus])

  // The looks, every character and extra for a visual check. Roles (the
  // emblem by the nameplate): the first demo agent is an HR manager, the
  // second a personal agent, the third a personal HR manager, the fourth an
  // EA Steward, the fifth a Steward with the staff tier, the sixth an EA HR
  // manager Steward with the staff tier, the eighth a Steward. Stored looks:
  // the HR manager and the Steward have none (the classic robot as before:
  // gold top hat, ID badge), the rest wear a character, colour and extras;
  // the seventh is unplugged. In the panels the Steward keeps its uploaded
  // picture; the last has only a gravatar, which never counts, so it wears
  // its character. Emitted on an interval like the real app's agent poll (a
  // one-shot can fire before the scene finishes booting and would be missed;
  // bus events aren't replayed).
  useEffect(() => {
    const agentsOnFloor = snapshot.people.filter((p) => p.isAgent)
    const [hr, personal, personalHr, eaSteward, stewardStaff, everything, plain, steward, styledRobot] =
      agentsOnFloor
    if (!hr) return
    const ids = (...people: Array<{ userId: string } | undefined>) =>
      people.flatMap((p) => (p ? [p.userId] : []))
    const looks: Array<[{ userId: string } | undefined, AgentStyle]> = [
      [personal, { character: 'bot', color: null, hat: 'none', cape: false, badge: false }],
      [personalHr, { character: 'blob', color: '#EC4899', hat: 'beanie', cape: false, badge: false }],
      [eaSteward, { character: 'cat', color: '#E0A800', hat: 'cap', cape: false, badge: true }],
      [stewardStaff, { character: 'ghost', color: '#8F86C9', hat: 'tophat', cape: true, badge: false }],
      [everything, { character: 'star', color: '#0284C7', hat: 'none', cape: true, badge: true }],
      [plain, { character: 'drop', color: '#65A30D', hat: 'cap', cape: false, badge: false }],
      [styledRobot, { character: 'robot', color: '#6D28D9', hat: 'tophat', cape: true, badge: true }],
    ]
    const styles: Record<string, AgentStyle> = Object.fromEntries(
      looks.flatMap(([person, style]) => (person ? [[person.userId, style] as const] : [])),
    )
    const working = ids(hr, personal, personalHr, eaSteward)
    let beat = 0
    const tick = () => {
      bus.emit({ kind: 'agentHr', userIds: ids(hr, personalHr, everything) })
      bus.emit({ kind: 'agentPersonal', userIds: ids(personal, personalHr) })
      bus.emit({ kind: 'agentEa', userIds: ids(eaSteward, everything) })
      bus.emit({
        kind: 'agentSteward',
        userIds: ids(eaSteward, stewardStaff, everything, steward),
        staffUserIds: ids(stewardStaff, everything),
      })
      bus.emit({ kind: 'agentStyle', styles })
      bus.emit({ kind: 'agentUnplugged', userIds: ids(plain) })
      // Every other beat an agent reports work: a thought on the floor, a ticker line.
      beat += 1
      if (beat % 2 === 0 && working.length) {
        const turn = beat / 2
        bus.emit({
          kind: 'agentWork',
          userId: working[turn % working.length],
          summary: DEMO_WORK[turn % DEMO_WORK.length],
        })
      }
    }
    const id = window.setInterval(tick, 3000)
    return () => window.clearInterval(id)
  }, [snapshot, bus])

  const agents = snapshot.people.filter((p) => p.isAgent).length
  const focused = snapshot.people.find((p) => p.userId === focusedId) ?? null
  const roomsOf = (userId: string): string[] =>
    snapshot.pulses
      .filter((p) => p.category === 'TEAM' && p.memberUserIds.includes(userId))
      .map((p) => p.name)
  const seatRoomName = Object.fromEntries(
    snapshot.people.map((p) => [p.userId, roomsOf(p.userId)[0] ?? (p.isAgent ? 'Agent Bay' : 'Commons')]),
  )
  const roomOrder = [
    ...snapshot.pulses.filter((p) => p.category === 'TEAM').map((p) => p.name),
    'Agent Bay',
  ]

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%', overflow: 'hidden' }}>
      <PhaserGame snapshot={snapshot} bus={bus} onFocus={setFocusedId} />
      <div style={badge}>
        <span style={{ color: theme.terracottaLite, fontWeight: 600 }}>DEMO</span>
        <span style={{ color: theme.textDim }}>
          {' '}
          · live choreography · {snapshot.people.length} people · {agents} agents
        </span>
      </div>
      <button style={peopleBtn} onClick={() => setDirectoryOpen((open) => !open)}>
        People
      </button>
      {directoryOpen && (
        <div style={directorySlot}>
          <PeopleDirectory
            people={snapshot.people}
            seatRoomName={seatRoomName}
            roomOrder={roomOrder}
            meId={null}
            focusedUserId={focusedId}
            onSelect={(userId) => {
              bus.emit({ kind: 'cameraFollow', userId })
              setFocusedId(userId)
            }}
            onClose={() => setDirectoryOpen(false)}
          />
        </div>
      )}
      {focused && (
        <DetailPanel
          key={focused.userId}
          person={focused}
          openTasks={0}
          rooms={roomsOf(focused.userId)}
          onClose={() => setFocusedId(null)}
        />
      )}
      <Ticker bus={bus} snapshot={snapshot} />
    </div>
  )
}

const badge: React.CSSProperties = {
  position: 'absolute',
  top: 14,
  left: 18,
  fontSize: 13,
  background: '#0e0f14cc',
  padding: '6px 12px',
  borderRadius: 8,
  pointerEvents: 'none',
}

const peopleBtn: React.CSSProperties = {
  position: 'absolute',
  top: 14,
  right: 18,
  fontSize: 12.5,
  fontWeight: 600,
  color: theme.cream,
  background: '#15161dd9',
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  padding: '6px 12px',
  cursor: 'pointer',
}

const directorySlot: React.CSSProperties = {
  position: 'absolute',
  top: 56,
  left: 18,
}
